import type { ThreemaUpdateInput } from '@bp/bp-graphql-types';
import { Button, Modal, ProfileIcon, showToast, Table, type TableColumns, useDefaultSorting } from '@bp/ui-components';
import { type DocumentType, type FragmentType, getFragmentData, graphql } from 'client/gql';
import type { ThreemaProps } from 'client/gql/graphql';
import { useIsDesktop } from 'hooks/useIsDesktop';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessToast } from 'utils/toasts';
import { ThreemaTableFragment } from './ThreemaTable';

const ThreemaAssignModalFragment = graphql(`
  fragment ThreemaAssignModal on Query {
    profiles(
      where: { organization: { uuid: $organizationUuid }, threemaConnection_NONE: { edge: { enabled: true } } }
    ) {
      uuid
      __typename
      email
      firstName
      lastName
      threemaId
      threemaConnection {
        __typename
        edges {
          properties {
            __typename
            workCredentialId
          }
        }
      }
    }
  }
`);

export const AssignThreemaToProfile = graphql(`
  mutation AssignThreemaToProfile($update: ThreemaUpdateInput!, $where: ThreemaWhere!) {
    updateThreemas(update: $update, where: $where) {
      threemas {
        uuid
        __typename
        profiles {
          uuid
          __typename
          email
          firstName
          lastName
          threemaId
          threemaConnection {
            __typename
            edges {
              properties {
                __typename
                workCredentialId
              }
            }
          }
        }
      }
    }
  }
`);

type ThreemaAssignModalTableType = DocumentType<typeof ThreemaAssignModalFragment>['profiles'][0];

export type ThreemaAssignModalProps = {
  query: FragmentType<typeof ThreemaAssignModalFragment> | undefined;
  isOpen: boolean;
  onRequestClose: () => void;
  threemaDetails: (ThreemaProps & { originalProfileUuid: string | null }) | null;
};

export const ThreemaAssignModal = ({ query, isOpen, onRequestClose, threemaDetails }: ThreemaAssignModalProps) => {
  const { t } = useTranslation();
  const fragment1 = getFragmentData(ThreemaAssignModalFragment, query);
  const isDesktop = useIsDesktop();

  // @ts-ignore - don't know how to type 'query' properly
  const fragment2 = getFragmentData(ThreemaTableFragment, query);

  const [{ fetching }, mutation] = useMutation(AssignThreemaToProfile);
  const data = fragment1?.profiles ?? [];

  const columns: TableColumns<ThreemaAssignModalTableType>[] = [
    {
      header: t('common.firstName'),
      id: 'firstName',
      accessorKey: 'firstName',
    },
    {
      header: t('common.lastName'),
      id: 'lastName',
      accessorKey: 'lastName',
    },
    {
      header: t('common.email'),
      id: 'email',
      accessorKey: 'email',
    },
  ];

  const { sorting, onSortingChange } = useDefaultSorting();

  const handleAssignClick = async (profileInput: ThreemaAssignModalTableType) => {
    if (!threemaDetails) return;
    const { originalProfileUuid, ...data } = threemaDetails;

    try {
      const update: ThreemaUpdateInput = {
        profiles: [
          {
            disconnect: [{ where: { node: { uuid: originalProfileUuid ?? '--' } } }],
            connect: [
              {
                edge: {
                  ...data,
                  enabled: true,
                },
                where: { node: { uuid: profileInput.uuid } },
              },
            ],
          },
        ],
      };

      await mutation({ update, where: { uuid: fragment2.threemas[0].uuid } });
      showSuccessToast(t('threema.assign_success'));
      onRequestClose();
    } catch (error) {
      console.error(error);
      showToast(t('threema.assign_error'), {
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} title={t('threema.assign_title')}>
      <Table<ThreemaAssignModalTableType>
        isDesktop={isDesktop}
        data-cy='threemaList'
        showActionBar
        isOnWhite={false}
        showBorderRadius
        showShadow
        showSort
        canScroll
        sorting={sorting}
        onSortingChange={onSortingChange}
        actionBarSettings={{
          showExpertFilter: true,
        }}
        emptyStateSettings={{
          icon: <ProfileIcon />,
          padding: 'xl',
        }}
        columns={columns}
        data={data}
        lastColWidth='100px'
        lastCol={(row) => {
          return (
            <Button
              hierarchy='primary'
              onClick={() => handleAssignClick(row.original)}
              fullWidth
              isLoading={fetching}
              disabled={!threemaDetails}
            >
              {t('threema.assign')}
            </Button>
          );
        }}
      />
    </Modal>
  );
};
